import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import notificationService from '@/services/notificationService';
import { Notification, NotificationType } from '@/types';
import { formatRelativeDate } from '@/utils/formatters';
import toast from 'react-hot-toast';

const NOTIFICATION_ICON: Record<NotificationType, string> = {
  [NotificationType.INFO]: 'info',
  [NotificationType.SUCCESS]: 'check_circle',
  [NotificationType.WARNING]: 'warning',
  [NotificationType.ERROR]: 'error',
  [NotificationType.CLAIM_UPDATE]: 'receipt_long',
  [NotificationType.PAYMENT_RECEIVED]: 'payments',
  [NotificationType.DOCUMENT_REQUIRED]: 'description',
  [NotificationType.CONTRACT_EXPIRY]: 'event_busy',
  [NotificationType.APPOINTMENT_REMINDER]: 'event',
};

const NotificationCenterPage: React.FC = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationService.getNotifications(),
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
      toast.success('Toutes les notifications marquées comme lues');
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
       queryClient.invalidateQueries({ queryKey: ['notifications'] });
       queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
    }
  });

  const notifications = data?.notifications || [];

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const markAllButton = notifications.length > 0 ? (
    <button
      type="button"
      onClick={() => markAllReadMutation.mutate()}
      disabled={markAllReadMutation.isPending}
      className="text-xs font-extrabold text-primary hover:text-green-600 transition-colors px-2 py-1"
    >
      Lire tout
    </button>
  ) : null;

  return (
    <AppShell title="Notifications" rightElement={markAllButton}>
      <div className="flex flex-col gap-0">
        {notifications.length > 0 ? (
          <ul className="flex flex-col">
            {notifications.map((notification: Notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (!notification.isRead) markReadMutation.mutate(notification.id);
                  }}
                  aria-label={`${notification.title}${notification.isRead ? '' : ', non lue'}`}
                  className={`flex w-full gap-4 p-4 text-left border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${notification.isRead ? 'opacity-70' : 'bg-primary/5'}`}
                >
                  <div className={`mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${notification.isRead ? 'bg-gray-100 dark:text-gray-400' : 'bg-primary/20 text-primary-dark'}`}>
                    <MaterialIcon icon={NOTIFICATION_ICON[notification.type] ?? 'notifications'} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className={`text-sm ${notification.isRead ? 'font-medium' : 'font-bold text-[#111813] dark:text-white'}`}>
                        {notification.title}
                      </h3>
                      <time
                        dateTime={notification.createdAt}
                        className="text-xs text-gray-400 whitespace-nowrap ml-2"
                      >
                        {formatRelativeDate(notification.createdAt)}
                      </time>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {notification.message}
                    </p>
                  </div>
                  {!notification.isRead && (
                    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-primary mt-2 shrink-0" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center px-4">
            <div className="bg-gray-100 dark:bg-white/5 p-4 rounded-full mb-4">
              <MaterialIcon icon="notifications_off" className="text-4xl text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">Aucune notification pour le moment</p>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default NotificationCenterPage;
